/*
 * DatePickerField.tsx
 * Reusable date picker field component with label, error handling, and custom styling.
 */
import React from 'react';
import DatePicker from '../DatePicker/DatePicker';
import { Skeleton } from '@radix-ui/themes';
import '../../styles/global.css';

// Props definition
interface DatePickerFieldProps {
	label: string;
	name: string;
	value: Date | undefined;
	onChange: (date: Date | undefined) => void;
	error?: string | null;
	placeholder?: string;
	required?: boolean;
	visibility?: boolean;
	readOnly?: boolean;
	loading?: boolean;
	showTime?: boolean;
}

// DatePickerField component
const DatePickerField: React.FC<DatePickerFieldProps> = ({
	label,
	name,
	value,
	onChange,
	error,
	placeholder,
	required,
	visibility,
	readOnly,
	loading,
	showTime = false,
}) => {
	// Génération d'un identifiant unique pour le datepicker
	const datePickerId = `datePicker-${name}`; // unique pour chaque champ
	// Gestion de la visibilité à true si non définie
	const visibilityInput = visibility ?? true;
	// Gestion de la propriété readOnly
	const readOnlyInput = readOnly ?? false;

	// State pour forcer le remount du DatePicker après le skeleton / visibilité
	const [renderKey, setRenderKey] = React.useState(0);

	// Si le champ devient visible, on incrémente renderKey pour recalculer le calendrier
	React.useEffect(() => {
		if (visibilityInput && !loading) {
			setRenderKey((prev) => prev + 1);
		}
	}, [visibilityInput, loading]);

	// Rendu du composant
	return (
		<div style={{ display: visibilityInput ? 'block' : 'none' }}>
			<label
				htmlFor={datePickerId}
				className="block text-sm font-medium text-gray-700 mb-1"
			>
				{label}
			</label>
			{loading ? (
				<Skeleton
					className="w-full h-10 rounded block bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<DatePicker
					key={renderKey}
					id={datePickerId}
					name={name}
					placeholder={placeholder}
					value={value || undefined}
					onChange={onChange}
					required={required}
					readOnly={readOnlyInput}
					showTime={showTime}
				/>
			)}
			{error && <p className="text-red-600 text-sm mt-1">{error}</p>}
		</div>
	);
};

// Export the component
export default DatePickerField;
